// HM-310 cluster 15: hook che deriva i 6 KPI Modulo 1 dashboard SPEC v2.6
// dai dati gia' fetchati (rooms, bookings window, kitchen orders).
// Niente nuovo endpoint backend, niente nuovi network round-trip.

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { fetchKitchenOrders } from '../../api/room-service.js';
import type { BookingDto, RoomDb } from '../../api/rooms.js';

export interface DashboardKpis {
  occupiedRooms: number;
  /** Totale camere (denominatore mostrato come "8/14" sul KPI Occupate). */
  totalRooms: number;
  /**
   * Proxy "incasso oggi": somma totalAmount dei bookings checkout-ed nelle
   * ultime 24h (l'API window e' 8h, quindi sara' sottostimato; vero
   * endpoint payments aggregati arrivera' in cluster 18 Cassa).
   */
  incomeTodayCents: number;
  /**
   * QA sweep 2026-07-22 (bug BASSO): check-in EFFETTUATI oggi — bookings
   * con checkedInAt nel giorno corrente (stessa definizione di
   * /planning/stats.checkInsToday). Prima contava gli arrivi PREVISTI
   * (confirmed futuri) e la label "Check-in oggi" mentiva: dashboard 0 vs
   * planning 5.
   */
  arrivalsToday: number;
  /** Arrivi previsti: confirmed con start tra now e fine giornata (subtrend "X in arrivo"). */
  arrivalsArriving: number;
  departuresToday: number;
  /** Subset di departuresToday in scadenza entro 1h (subtrend "X in scadenza"). */
  departuresExpiringSoon: number;
  pendingOrders: number;
  /** Ordini RS in stato preparing (subtrend "in preparazione"). */
  ordersPreparing: number;
  roomsToClean: number;
}

interface Args {
  rooms: RoomDb[];
  bookings: BookingDto[];
  now: Date;
}

export function useDashboardKpi({ rooms, bookings, now }: Args): DashboardKpis {
  const ordersQuery = useQuery({
    queryKey: ['room-service', 'orders'],
    queryFn: fetchKitchenOrders,
    staleTime: 30_000,
  });

  return useMemo(() => {
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(now);
    endOfDay.setHours(23, 59, 59, 999);
    const oneHourMs = 60 * 60 * 1000;

    let arrivalsToday = 0;
    let arrivalsArriving = 0;
    let departuresToday = 0;
    let departuresExpiringSoon = 0;
    let incomeTodayCents = 0;

    for (const b of bookings) {
      const start = new Date(b.startAt);
      const end = new Date(b.endAt);
      // Check-in EFFETTUATO oggi: checkedInAt nel giorno corrente (vale
      // sia per checked_in che per checked_out gia' usciti).
      const checkedInAt = b.checkedInAt ? new Date(b.checkedInAt) : null;
      if (checkedInAt && checkedInAt >= startOfDay && checkedInAt <= endOfDay) {
        arrivalsToday += 1;
      }
      if (b.status === 'confirmed' && start >= now && start <= endOfDay) {
        arrivalsArriving += 1;
      }
      if (b.status === 'checked_in' && end >= now && end <= endOfDay) {
        departuresToday += 1;
        if (end.getTime() - now.getTime() <= oneHourMs) {
          departuresExpiringSoon += 1;
        }
      }
      if (b.status === 'checked_out' && end >= startOfDay) {
        // totalAmount in EUR string ("123.45") -> centesimi
        const cents = Math.round(Number(b.totalAmount) * 100);
        if (Number.isFinite(cents)) incomeTodayCents += cents;
      }
    }

    const occupiedRooms = rooms.filter((r) => r.status === 'occupied').length;
    const roomsToClean = rooms.filter((r) => r.status === 'dirty').length;
    const allOrders = ordersQuery.data?.orders ?? [];
    const pendingOrders = allOrders.filter((o) => o.status === 'pending').length;
    const ordersPreparing = allOrders.filter((o) => o.status === 'preparing').length;

    return {
      occupiedRooms,
      totalRooms: rooms.length,
      incomeTodayCents,
      arrivalsToday,
      arrivalsArriving,
      departuresToday,
      departuresExpiringSoon,
      pendingOrders,
      ordersPreparing,
      roomsToClean,
    };
  }, [rooms, bookings, now, ordersQuery.data]);
}

/** Formatta cents -> "1.247,80 €" */
export function formatEuroCents(cents: number): string {
  const eur = cents / 100;
  return `${eur.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

/** Formatta cents -> "€ 1.240" (intero, formato compatto KPI strip mockup). */
export function formatEuroCompact(cents: number): string {
  const eur = Math.round(cents / 100);
  return `€ ${eur.toLocaleString('it-IT')}`;
}
